/** @jsx jsx */
import { Container, Flex, Grid, Loader } from "@eworkit/iris";
import { ParticipantList, Conversation, ConversationOptionsPanel } from "@eworkit/messenger";
import { FC, useCallback } from "react";
import { useMedia } from "react-use";
import { Card, jsx, ThemeUICSSObject } from "theme-ui";

import { MEDIA_SIZE_SM } from "@/core/media";
import { JobApplication } from "@/data/job-applications/types/JobApplication";
import { useRouter } from "@/router";
import { useApplicationConversation } from "@/views-new/chat/useConversation";
import { router } from "@/views/router";

const MESSAGE_TAB_MIN_HEIGHT = 400;

const messageTabStyles: ThemeUICSSObject = {
  height: "100%",
  minHeight: MESSAGE_TAB_MIN_HEIGHT,
  alignItems: "flex-start",
  "&>*": { borderRadius: "lg" },
};

const conversationCardStyles: ThemeUICSSObject = {
  minHeight: MESSAGE_TAB_MIN_HEIGHT,
  height: "100%",
  pt: "sm",
  backgroundColor: "n0",
  alignItems: "center",
  justifyContent: "center",
  display: "flex",
  borderStyle: "solid",
  borderColor: "n100",
  borderWidth: 1,
};

interface JobApplicationDetailsMessagesTabProps {
  jobApplication: JobApplication;
}

export const JobApplicationDetailsMessagesTab: FC<JobApplicationDetailsMessagesTabProps> = ({ jobApplication }) => {
  const { conversationId } = useApplicationConversation(jobApplication.id);
  const { push } = useRouter<{}, {}>();

  const isSmallScreen = useMedia(MEDIA_SIZE_SM);

  const goToMessageCenter = useCallback(() => {
    if (conversationId) {
      push(router.get("message-center.conversation", { conversationId }).path);
    }
  }, [conversationId]);

  return (
    <Container sx={{ height: "100%" }}>
      <Flex flexDirection="column" sx={{ width: "100%", height: "100%", boxSizing: "border-box" }}>
        {conversationId ? (
          <Grid columns={isSmallScreen ? "auto" : "auto 1fr"} sx={messageTabStyles} gap="sm">
            <Flex flexDirection="column" gap="sm" sx={{ gridRow: isSmallScreen ? 2 : 1 }}>
              <ConversationOptionsPanel conversationId={conversationId} goToMessageCenter={goToMessageCenter} />
              <ParticipantList conversationId={conversationId} />
            </Flex>
            <Card
              sx={{
                ...conversationCardStyles,
                px: isSmallScreen ? 0 : "md",
              }}>
              <Conversation id={conversationId} />
            </Card>
          </Grid>
        ) : (
          <Flex pt="xl" justifyContent="center" alignItems="center">
            <Loader size="xl" />
          </Flex>
        )}
      </Flex>
    </Container>
  );
};
